import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { useVisibleCategories } from '@/hooks/useVisibleCategories';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  HelpCircle, Search, Book, FileText, Calculator, Shield,
  ChevronDown, ChevronRight, ArrowLeft, Upload, Building2,
  Zap, Award, Download, ExternalLink, Leaf, TrendingUp,
  Lightbulb, FileSpreadsheet, X
} from 'lucide-react';

const HelpCentrePage = () => {
  const { user, currentOrg } = useOutletContext();
  const { flatCategories = [] } = useVisibleCategories(currentOrg);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [guides, setGuides] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false to show content immediately

  // Reorganized categories matching user's request
  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      color: 'bg-blue-500',
      description: 'Set up your account, create organisations, and learn the basics'
    },
    {
      id: 'compliance',
      title: 'Compliance & Rules',
      icon: Shield,
      color: 'bg-emerald-500',
      description: 'AASB S2, Scope 1/2/3 explained in plain English, audit requirements'
    },
    {
      id: 'state-guides',
      title: 'National State Guides',
      icon: Building2,
      color: 'bg-indigo-500',
      description: 'State-specific data sourcing tips for each Australian territory'
    },
    {
      id: 'tenders',
      title: 'Tender Readiness',
      icon: Award,
      color: 'bg-amber-500',
      description: 'Using Lucent Sky ESG reports to win government and corporate contracts'
    },
    {
      id: 'data-entry',
      title: 'Data Entry Tips',
      icon: Upload,
      color: 'bg-cyan-500',
      description: 'Best practices for logging emissions, CSV imports, and evidence'
    },
    {
      id: 'downloads',
      title: 'Downloads',
      icon: Download,
      color: 'bg-slate-600',
      description: 'Templates, sample reports, and resources'
    }
  ];

  // Articles organized by category
  const articles = {
    'getting-started': [
      {
        id: 'create-org',
        title: 'How do I create my first organisation?',
        content: `After signing up, you'll be guided through our Setup Wizard. Simply enter your organisation's name, ABN, State/Territory, and sector.

**Why State matters:** The State selection determines which 2026 National Greenhouse Accounts emission factors are applied to your calculations. For example, Victoria has higher electricity emissions (0.82 kg CO₂-e/kWh) than Tasmania (0.15 kg CO₂-e/kWh).

**Steps:**
1. Click "Complete Setup" on your Dashboard
2. Enter your organisation name and ABN
3. Select your State/Territory
4. Choose your industry sector
5. Add your first emission entry to complete setup`
      },
      {
        id: 'free-vs-pro',
        title: "What's the difference between Free Trial and Professional?",
        content: `**Free Trial:**
• Up to 5 emission entries
• Watermarked sample reports
• Basic grant snapshot
• State-specific emission factors

**Professional (From $22/month):**
• Unlimited entries
• Official audit-ready PDF reports
• Full Grant Readiness Vault
• Evidence Vault with document linking
• Reduction target tracking
• Supplier invitation tools
• Priority email support

Both tiers use official NGA factors, applied according to each financial year for accurate calculations.`
      },
      {
        id: 'multiple-orgs',
        title: 'Can I manage multiple businesses?',
        content: `Yes! Lucent Sky ESG supports multi-entity management.

**Pricing:**
• Primary organisation: from $11/month (Standard) or $22/month (Pro)
• Additional entities: 20% discount on any tier

**Each organisation has:**
• Separate emission tracking
• Own Evidence Vault
• Individual PDF reports
• Unique public profile URL

**To add a new organisation:**
1. Go to Dashboard
2. Click the organisation dropdown (top-left)
3. Select "Add Organisation"
4. Complete the setup wizard for the new entity`
      }
    ],
    'compliance': [
      {
        id: 'emission-factors',
        title: 'What emission factors does Lucent Sky ESG use?',
        content: `All calculations use the **2026 National Greenhouse Accounts (NGA)** factors published by the Australian Government Department of Climate Change, Energy, the Environment and Water.

**Electricity Factors by State (kg CO₂-e per kWh):**
| State | Factor |
|-------|--------|
| QLD | 0.67 |
| NSW/ACT | 0.64 |
| VIC | 0.82 |
| SA | 0.25 |
| WA | 0.51 |
| TAS | 0.15 |
| NT | 0.54 |

**Other Factors:**
• Petrol: 2.31 kg CO₂-e per litre
• Diesel: 2.68 kg CO₂-e per litre
• Natural Gas: 51.53 kg CO₂-e per GJ
• Waste (landfill): 1.2 kg CO₂-e per kg

These factors are stored in our database and can be updated annually by administrators when new NGA workbooks are released.`
      },
      {
        id: 'scopes',
        title: 'How are Scope 1, 2, and 3 emissions classified?',
        content: `Lucent Sky ESG follows the **GHG Protocol** classification:

**Scope 1 – Direct Emissions** (Orange badge)
• Fuel combustion (petrol, diesel, LPG)
• Natural gas combustion
• Your company directly burns the fuel

**Scope 2 – Purchased Energy** (Amber badge)
• Electricity from the grid
• Your company buys the energy

**Scope 3 – Indirect Emissions** (Green badge)
• Business travel (flights, hotels)
• Waste disposal
• Water supply & treatment
• Supplier emissions (coming soon)

**For SMEs:** Scope 1 and 2 are typically required for compliance. Scope 3 is recommended for larger organisations but marked as "optional" in our Data Completeness tracker.`
      },
      {
        id: 'frameworks',
        title: 'What compliance frameworks do reports support?',
        content: `Our reports are designed to meet:

**AASB S2 Climate-related Disclosures**
• Required for large Australian companies from 2025
• Full methodology citation in reports

**Queensland Procurement Policy (QPP) 2026**
• Environmental reporting for government tenders
• Evidence trail for audit requirements

**NGER (National Greenhouse & Energy Reporting)**
• Calculation methodology alignment
• Category classifications

Each PDF report includes a **Methodology & Compliance Statement** page with full factor citations and calculation breakdowns.`
      },
      {
        id: 'factor-versioning',
        title: 'What happens when emission factors change?',
        content: `Lucent Sky ESG uses **Factor Versioning** for audit compliance:

**How it works:**
• Each emission entry stores the factor value used at creation time
• Updating factors only affects NEW entries
• Historical data remains unchanged

**Why this matters:**
• Your FY 2025/26 report uses 2026 factors
• If 2027 factors are released, past entries stay accurate
• Auditors can verify calculations match reported factors

**For Admins:**
SuperAdmins can add new year factors via:
Admin Dashboard → Factor Manager → Add Factor`
      }
    ],
    'state-guides': [
      {
        id: 'nsw-guide',
        title: 'New South Wales - Data Sourcing Guide',
        content: `**Electricity Providers:**
• **Ausgrid**: Bills show kWh in 'Electricity Usage' section. Check for solar feed-in separately.
• **Endeavour Energy**: Look for 'Total Usage' in kWh on page 1.
• **Essential Energy**: Rural customers - check meter reading dates align with billing period.
• **AGL/Origin/EnergyAustralia**: Retailer bills show consumption clearly. NMI number useful for verification.

**Gas Providers:**
• **Jemena**: Bills show MJ - divide by 1000 to get GJ for Lucent Sky ESG.
• **ActewAGL** (regional): Combined electricity/gas bills available.

**Water:**
• **Sydney Water**: Look for kL usage on quarterly bills.
• **Hunter Water**: Similar format to Sydney Water.

**Tips:**
• NSW electricity factor is 0.64 kg CO₂-e/kWh (2026 NGA)
• Many retailers offer 12-month usage summaries - great for annual reporting
• Solar generation should be excluded from consumption figures`
      },
      {
        id: 'vic-guide',
        title: 'Victoria - Data Sourcing Guide',
        content: `**Electricity Providers:**
• **AusNet Services**: Distribution network - NMI starts with 6305
• **CitiPower/Powercor**: Inner Melbourne and regional - detailed usage graphs on bills
• **Jemena**: Northern suburbs - bills show daily average usage
• **United Energy**: Southeast Melbourne - smart meter data available online

**Retailer Tips:**
• Origin/AGL/EnergyAustralia: All provide 12-month comparisons
• Many Victorian retailers offer GreenPower options - track separately for Scope 2 reductions

**Gas:**
• **Australian Gas Networks**: Bills show MJ usage
• Most Victorian homes use natural gas for heating - capture winter spikes

**Water:**
• **Melbourne Water**: Wholesale charges on bills
• **Yarra Valley/South East/City West Water**: Retail bills show kL clearly

**Important:**
• Victoria has the highest electricity emission factor: 0.82 kg CO₂-e/kWh
• This makes electricity reduction a high priority for Victorian businesses`
      },
      {
        id: 'qld-guide',
        title: 'Queensland - Data Sourcing Guide',
        content: `**Electricity Networks:**
• **Energex**: Southeast QLD (Brisbane, Gold Coast, Sunshine Coast)
• **Ergon Energy**: Regional and rural Queensland
• Both show NMI and detailed consumption

**Reading Your Bill:**
• Look for 'Total kWh' in the usage summary
• Peak/off-peak breakdown useful for load shifting strategies
• Solar customers: Check 'Energy Exported' separately

**Gas:**
• Natural gas less common in QLD due to climate
• **APT/APA Group**: Industrial gas customers

**Water:**
• **Urban Utilities**: Brisbane, Ipswich, Scenic Rim, Somerset, Lockyer Valley
• **Unitywater**: Sunshine Coast, Moreton Bay
• Bills show kL usage by quarter

**Tips:**
• QLD factor is 0.67 kg CO₂-e/kWh
• Air conditioning is typically the largest electricity use - check summer bills
• ecoBiz Queensland offers free sustainability coaching`
      },
      {
        id: 'wa-guide',
        title: 'Western Australia - Data Sourcing Guide',
        content: `**Electricity:**
• **Western Power**: Network operator for South West Interconnected System
• **Horizon Power**: Regional WA - Pilbara, Kimberley, Goldfields
• **Synergy**: Main retailer for residential/business

**Bill Reading:**
• Synergy bills show clear kWh totals
• Smart meter customers can access 30-minute interval data
• Business customers may have demand (kVA) charges separately

**Gas:**
• **ATCO Gas**: Network operator
• **Alinta Energy/Kleenheat**: Major retailers
• Bills show MJ - convert to GJ by dividing by 1000

**Water:**
• **Water Corporation**: Statewide provider
• Bills show kL consumption
• Winter usage often lower due to climate

**Tips:**
• WA factor is 0.51 kg CO₂-e/kWh (lower than east coast states)
• Mining sector should use site-specific data from ESO metering
• Remote locations may have higher factors for diesel generation`
      },
      {
        id: 'sa-guide',
        title: 'South Australia - Data Sourcing Guide',
        content: `**Electricity:**
• **SA Power Networks**: Sole distribution network
• Major retailers: AGL, Origin, Simply Energy, Lumo

**SA has Australia's Lowest Grid Factor:**
• 0.25 kg CO₂-e/kWh (2026 NGA)
• High renewable penetration from wind and solar
• Great opportunity for electrification strategies

**Gas:**
• **Australian Gas Infrastructure Group (AGIG)**: Network
• **AGL/Origin**: Main retailers
• Bills show MJ usage

**Water:**
• **SA Water**: Statewide provider
• Murray-Darling Basin businesses - irrigation usage tracked separately

**Tips:**
• SA businesses have a competitive advantage in carbon accounting
• Consider electric vehicles and heat pumps - low grid factor makes these very effective
• Rooftop solar common - track generation and consumption separately`
      },
      {
        id: 'tas-guide',
        title: 'Tasmania - Data Sourcing Guide',
        content: `**Electricity:**
• **TasNetworks**: Distribution and transmission
• **Aurora Energy**: Government-owned retailer
• Some competition from mainland retailers

**Tasmania has Australia's Cleanest Grid:**
• 0.15 kg CO₂-e/kWh (2026 NGA)
• Hydro-dominated with Basslink interconnector to Victoria
• Electrification is the clear path to decarbonisation

**Gas:**
• Limited natural gas infrastructure in Tasmania
• Most businesses use electricity or LPG

**Water:**
• **TasWater**: Statewide provider
• Irrigation significant in agriculture sector

**Tips:**
• Tasmanian businesses should highlight their low-carbon electricity advantage
• Export businesses can use this as a sustainability selling point
• Focus emission reduction efforts on Scope 1 (fuel) and Scope 3`
      },
      {
        id: 'act-guide',
        title: 'ACT - Data Sourcing Guide',
        content: `**Electricity:**
• **Evoenergy**: Distribution network (formerly ActewAGL Distribution)
• **Origin/ActewAGL/EnergyAustralia**: Main retailers
• ACT uses NSW/ACT combined factor: 0.64 kg CO₂-e/kWh

**ACT Climate Commitments:**
• 100% renewable electricity target achieved
• Many businesses can claim zero Scope 2 if using local grid
• Check with retailer for renewable certification

**Gas:**
• **Evoenergy**: Also operates gas network
• ACT planning to phase out gas for new developments

**Water:**
• **Icon Water**: ACT's water and sewerage provider
• Bills show kL consumption

**Tips:**
• ACT businesses benefit from territory-level renewable procurement
• Government contracts often require carbon reporting
• Canberra has strong public transport - consider Scope 3 travel impacts`
      },
      {
        id: 'nt-guide',
        title: 'Northern Territory - Data Sourcing Guide',
        content: `**Electricity:**
• **Power and Water Corporation**: Government-owned utility
• Darwin-Katherine, Alice Springs, and remote systems
• Factor: 0.54 kg CO₂-e/kWh

**Unique Considerations:**
• Many remote locations use diesel generation
• Mining and resources sector significant
• Solar potential excellent but battery storage often needed

**Gas:**
• Gas production significant in NT
• Some businesses use natural gas directly

**Water:**
• **Power and Water Corporation**: Also provides water
• Wet/dry season affects usage patterns significantly

**Tips:**
• Air conditioning is a major electricity use - optimise HVAC
• Tourism businesses should track Scope 3 visitor travel
• Indigenous businesses may access specific grant programs`
      }
    ],
    'tenders': [
      {
        id: 'tender-intro',
        title: 'Why Carbon Reports Win Tenders',
        content: `**The Competitive Advantage:**

Australian government and corporate procurement is increasingly weighted toward sustainability. Here's why Lucent Sky ESG reports help you win:

**Government Contracts (QPP 2026):**
• Queensland: QPP requires environmental reporting for contracts over $1M
• NSW: Sustainable Procurement Policy mandates sustainability assessments
• Victoria: Social Procurement Framework includes environmental criteria
• Federal: Climate Active certification recognised in tenders

**Corporate Supply Chains:**
• Major retailers (Woolworths, Coles) require supplier sustainability data
• Mining companies need contractor carbon footprints
• Property developers increasingly carbon-conscious

**What Tender Assessors Look For:**
1. **Verified data** - Not just claims, but evidence-backed numbers
2. **Methodology** - Which emission factors? What standards?
3. **Progress** - Are you improving year-on-year?
4. **Commitment** - Reduction targets and action plans

Lucent Sky ESG reports provide all four elements in a professional PDF.`
      },
      {
        id: 'using-reports',
        title: 'How to Use Your Report in Tenders',
        content: `**Preparing Your Submission:**

**Step 1: Generate Your Report**
Dashboard → Generate Report → Select Financial Year → Download PDF

**Step 2: Key Sections to Reference**
• **Executive Summary**: Total emissions, intensity metrics
• **Methodology Statement**: Cite NGA 2026 factors
• **Category Breakdown**: Show you track all relevant scopes
• **Evidence Index**: List of supporting documents

**Step 3: Tender Response Language**
Example paragraph for sustainability section:

*"[Company Name] tracks greenhouse gas emissions using Lucent Sky ESG, a platform compliant with 2026 National Greenhouse Accounts methodology. Our total Scope 1 and 2 emissions for FY 2025/26 were [X] tCO₂-e, representing [Y]% reduction from our baseline year. Full methodology and evidence are available in our attached Emissions Report."*

**Step 4: Attach Evidence**
• Include full PDF report as appendix
• Reference page numbers in your response
• Highlight public verification URL if enabled`
      },
      {
        id: 'qpp-specific',
        title: 'Queensland Procurement Policy (QPP) Requirements',
        content: `**QPP 2026 Environmental Reporting:**

The Queensland Government requires suppliers to demonstrate environmental credentials for contracts over $1 million.

**What You Need:**
• Emissions baseline and tracking
• Reduction targets (if any)
• Evidence trail (bills, receipts)
• Methodology statement

**How Lucent Sky ESG Helps:**

| Requirement | Lucent Sky ESG Feature |
|-------------|---------------------|
| Track emissions | Dashboard + Reports |
| 2026 NGA factors | Built-in calculations |
| Evidence storage | Evidence Vault |
| Audit trail | Entry history + documents |
| Public verification | Shareable URL |

**Pro Tip:**
Enable your Public Verification URL before submitting. Assessors can independently verify your data, adding credibility to your submission.

**Common QPP Questions Answered by Reports:**
• "What is your carbon footprint?" → Executive Summary
• "What methodology do you use?" → Methodology Statement
• "Can you prove these numbers?" → Evidence Index + Public URL`
      },
      {
        id: 'corporate-supply',
        title: 'Corporate Supply Chain Requirements',
        content: `**Major Corporate Sustainability Programs:**

**Retail Sector:**
• **Woolworths Group**: Requires supplier sustainability assessments
• **Wesfarmers (Coles, Bunnings)**: ESG questionnaires for suppliers
• **IKEA**: Strict environmental requirements for all suppliers

**Mining & Resources:**
• **BHP**: Sustainable procurement standards
• **Rio Tinto**: Supplier carbon footprint disclosure
• **Fortescue**: Clean energy requirements for contractors

**Construction & Property:**
• **Lendlease**: Green Star requirements flow to subcontractors
• **Mirvac**: Net positive carbon commitments
• **Stockland**: Sustainable value chain program

**How to Position Your Business:**
1. Track Scope 1 & 2 as minimum
2. Add Scope 3 travel/waste for competitive edge
3. Set reduction targets (even modest ones show commitment)
4. Maintain evidence vault for audits
5. Share public profile for transparency

**Response Template:**
*"As part of our sustainability commitment, we track all operational emissions using Lucent Sky ESG. Our current carbon footprint of [X] tCO₂-e is verified against 2026 NGA methodology. We are committed to [Y]% reduction by [Year] and welcome value chain audits."*`
      }
    ],
    'data-entry': [
      {
        id: 'categories',
        title: 'What emission categories can I track?',
        content: `**Scope 1 (Direct):**
• fuel_petrol – Petrol (litres)
• fuel_diesel – Diesel (litres)
• fuel_lpg – LPG (litres)
• natural_gas – Natural Gas (GJ)

**Scope 2 (Purchased Energy):**
• electricity – Grid Electricity (kWh)
• electricity_greenpower – GreenPower (kWh) – 0 emissions

**Scope 3 (Indirect):**
• flight_short – Short-haul flights <3hrs
• flight_medium – Medium-haul 3-6hrs
• flight_long – Long-haul >6hrs
• hotel_stay – Accommodation (nights)
• waste_landfill – Landfill waste (tonnes)
• waste_recycling – Recycling (tonnes)
• waste_compost – Compost/organics (tonnes)
• water – Mains water (kL)`
      },
      {
        id: 'csv-import',
        title: 'How do I bulk import data via CSV?',
        content: `**Step 1: Download Template**
Go to Dashboard → Import → "Download 2026 Template"

**Step 2: Fill in your data**
Columns: Date, Category, Quantity, Unit, Notes

**Date formats accepted:**
• YYYY-MM-DD (2024-07-15)
• DD/MM/YYYY (15/07/2024)

**Step 3: Upload & Preview**
Upload your CSV and review the preview table. The system shows:
• Validated rows (green check)
• Errors (red, with explanation)
• Scope breakdown preview
• Total tCO₂-e calculation

**Step 4: Confirm Import**
Click "Import X Entries" to save. Only valid rows are imported.`
      },
      {
        id: 'evidence-vault',
        title: 'What documents should I upload?',
        content: `**Recommended evidence by category:**

**Electricity:**
• Utility bills (PDF)
• Smart meter reports
• Solar generation certificates

**Fuel:**
• Fuel card statements
• Petrol receipts
• Fleet management reports

**Natural Gas:**
• Gas bills
• Meter readings

**Waste:**
• Waste collection invoices
• Weighbridge dockets
• Recycling certificates

**Tips:**
• Link each document to a specific emission entry
• Use clear file names (e.g., "Electricity_Jul2024.pdf")
• Max file size: 10MB per document`
      },
      {
        id: 'xero-myob-import',
        title: 'How do I import from Xero or MYOB?',
        content: `**Exporting from Xero:**

**Step 1: Export General Ledger**
1. In Xero, go to Reports → Accounting → General Ledger
2. Set date range for your reporting period (e.g., FY 2025/26)
3. Click Export → CSV

**Step 2: Identify Relevant Accounts**
Look for these account names in your export:
• "Electricity" or "Utilities - Electricity"
• "Fuel & Oil" or "Motor Vehicle Expenses"
• "Gas" or "Utilities - Gas"
• "Water Rates"
• "Waste Disposal"

**Step 3: Map to Lucent Sky ESG Format**
Our CSV importer auto-detects these patterns:

| Xero Account Name | Lucent Sky Category |
|-------------------|-------------------|
| Electricity | electricity |
| Fuel & Oil | fuel_diesel |
| Motor Vehicle - Fuel | fuel_petrol |
| Gas | natural_gas |
| Water | water |

**Step 4: Prepare Your CSV**
1. Open our template: Dashboard → Import → Download Template
2. Copy Date, Amount/Quantity from Xero export
3. Add Category based on mapping above
4. Upload to Lucent Sky ESG

**Pro Tip:** Export Xero's "Expense Claims" report for travel reimbursements (flights, hotels).

---

**Exporting from MYOB:**

**Step 1: Export Transaction Journal**
1. In MYOB, go to Reports → Accounts → Transaction Journal
2. Filter by date range and accounts (Utilities, Motor Vehicle)
3. Export to Excel/CSV

**Step 2: Similar mapping applies**
MYOB account names are similar to Xero. Match to our categories as above.

**Need Help?**
Contact support@lucentsky.com.au with your exported file and we'll help you map it correctly.`
      },
      {
        id: 'receipt-scanner',
        title: 'How does the Receipt Scanner work?',
        content: `**OCR Lite (Receipt Scanner)**

Our Receipt Scanner uses client-side OCR to extract data from photos of fuel receipts and utility bills.

**How to use:**
1. Click "Scan Receipt" on the Dashboard
2. Take a photo or upload an image
3. The scanner extracts Date, Quantity, and Units
4. Review and confirm the extracted data
5. Click "Apply to Form" to pre-fill your entry

**Supported documents:**
• Fuel receipts (extracts Litres)
• Electricity bills (extracts kWh)
• Gas bills (extracts MJ/GJ)

**Tips for best results:**
• Use good lighting
• Keep the camera steady
• Include the date and quantity clearly in frame
• Avoid shadows and glare

**Low Confidence Results:**
If the scanner can't read your receipt clearly, it will highlight the fields in amber. Simply type the correct values manually.

**Privacy:**
All scanning happens on your device – no images are sent to our servers.`
      },
      {
        id: 'units',
        title: 'What units should I use?',
        content: `**Correct units by category:**

| Category | Unit | Example |
|----------|------|---------|
| Electricity | kWh | 1,500 kWh |
| Natural Gas | GJ | 12.5 GJ |
| Petrol/Diesel/LPG | Litres (L) | 500 L |
| Waste | Tonnes | 2.5 t |
| Water | Kilolitres (kL) | 150 kL |
| Flights | Passengers or pax-km | 3 passengers |
| Hotels | Nights | 5 nights |

**Converting from bills:**
• Electricity: Already in kWh on most bills
• Gas: Check if bill shows GJ or MJ (1 GJ = 1000 MJ)
• Water: 1 kL = 1,000 litres`
      }
    ],
    'growth': [
      {
        id: 'reduction-targets',
        title: 'How do I set a reduction target?',
        content: `**Setting your target:**
1. Go to Dashboard
2. Find "Reduction Target Progress" widget
3. Click "Set Target"
4. Choose:
   • **Baseline Year** – The year to measure against
   • **Reduction %** – Your target (e.g., 20%)
   • **Target Year** – When to achieve it (e.g., 2030)

**The widget shows:**
• Baseline emissions (auto-calculated from your data)
• Target emissions (baseline minus reduction %)
• Current year emissions
• Progress percentage
• On Track / Behind Target status

**Tip:** Align with the Paris Agreement's 1.5°C pathway by targeting 50% reduction by 2030.`
      },
      {
        id: 'grants',
        title: 'How does the Grant Tool work?',
        content: `The Grant Tool matches your organisation against available sustainability grants.

**How matching works:**
• Your State/Territory
• Industry sector
• Organisation size
• Current emission categories

**Free users:** See a snapshot of potentially matching grants

**Pro users:** Access the Grant Readiness Vault with:
• Full eligibility criteria
• Required evidence checklist
• Application deadline tracking
• Evidence Report PDF generator

**Popular grants tracked:**
• QLD Tradies Manufacturing Grant
• NSW Emissions Reduction Fund
• VIC Sustainability Fund
• Federal Clean Energy programs`
      },
      {
        id: 'supplier-invitations',
        title: 'How do I invite suppliers for Scope 3 data?',
        content: `**Pro feature: Supplier Invitation Tool**

Simplify Scope 3 data collection by inviting your suppliers to Lucent Sky ESG.

**To invite a supplier:**
1. Go to Dashboard → Suppliers (coming soon)
2. Click "Invite Supplier"
3. Enter their email and company name
4. Add a personalised message
5. Send invitation

**What happens:**
• Supplier receives branded email
• They can sign up for free
• Once they track their emissions, you can request shared data
• Streamlines value chain reporting

**Benefits:**
• Accurate Scope 3 data
• Automated data sharing
• Build sustainable value chains`
      },
      {
        id: 'insights',
        title: 'How do auto-advisory insights work?',
        content: `Your PDF report includes **Recommended Actions** based on your data.

**How it works:**
The system analyses your top emission sources and provides tailored advice:

**If Electricity is highest:**
→ "Renewable Energy Upgrade" – Switch to GreenPower or install solar

**If Fuel is highest:**
→ "Fleet Efficiency Program" – EV transition, route optimisation, eco-driving

**If Natural Gas is highest:**
→ "Electrification Strategy" – Heat pumps, electric alternatives

**If Waste is significant:**
→ "Waste Reduction & Recycling" – Increase diversion from landfill

**Every report includes:**
→ "Set a Science-Based Target" – Align with Paris Agreement pathway

These insights are automatically generated—no action needed from you.`
      }
    ]
  };

  // Downloads section content
  const downloads = [
    {
      id: 'csv-template',
      title: 'Emissions Bulk Import Template',
      description: 'Official CSV template for bulk importing manual emissions data (Scope 1 and 2 categories)',
      icon: FileSpreadsheet,
      action: async () => {
        try {
          const templateRows = flatCategories.map(cat => {
            const today = new Date();
            const startDate = today.toISOString().split('T')[0];

            // Set End Date for period-based emissions (Electricity, Gas, Water, Waste)
            const isPeriodBased = cat.value.includes('electricity') ||
              cat.value === 'natural_gas' ||
              cat.value === 'water' ||
              cat.value.includes('waste');

            let endDate = '';
            if (isPeriodBased) {
              const end = new Date(today);
              end.setMonth(end.getMonth() + 1);
              endDate = end.toISOString().split('T')[0];
            }

            return {
              'Category': cat.value,
              'Name/Source': cat.label,
              'Start Date (YYYY-MM-DD)': startDate,
              'End Date (YYYY-MM-DD)': endDate,
              'Quantity': '10.0', // Example quantity
              'Unit': cat.unit,
              'Notes': `Example for ${cat.label}`
            };
          });

          // If no categories yet (loading or empty), provide a fallback header
          const csvData = templateRows.length > 0
            ? Papa.unparse(templateRows)
            : 'Category,Name/Source,Start Date (YYYY-MM-DD),End Date (YYYY-MM-DD),Quantity,Unit,Notes';

          const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
          const { saveAs } = await import('file-saver');
          saveAs(blob, 'emission_entries_template.csv');
          toast.success('Generated customized template based on your active factors');
        } catch (error) {
          console.error('Error generating template:', error);
          toast.error('Failed to generate template file');
        }
      },
      buttonText: 'Download CSV'
    },
    {
      id: 'sample-report',
      title: 'Sample 2026 Compliance Report',
      description: 'See what a Professional audit-ready PDF report looks like with charts, methodology, and evidence index',
      icon: FileText,
      action: () => toast.info('Sample report coming soon! Generate a real report from your Dashboard.'),
      buttonText: 'View Sample'
    }
  ];

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        // Fetch help guides directly from Supabase
        const { data: guidesData, error } = await supabase
          .from('help_guides')
          .select('*, help_guide_items(*)');

        if (!error && guidesData) {
          setGuides(guidesData);
        }
      } catch (error) {
        // Use built-in articles - no error handling needed
      }
      setLoading(false);
    };
    fetchGuides();
  }, []);

  // Search across all articles
  const searchResults = searchTerm.length >= 2
    ? Object.entries(articles).flatMap(([catId, catArticles]) =>
      catArticles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase())
      ).map(article => ({ ...article, categoryId: catId }))
    )
    : [];

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    setActiveArticle(null);
    setSearchTerm('');
  };

  const handleArticleClick = (article) => {
    setActiveArticle(article);
  };

  const handleBack = () => {
    if (activeArticle) {
      setActiveArticle(null);
    } else {
      setActiveCategory(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#003B5C] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-56px)] bg-[#F8F9FA]" data-testid="help-centre-page">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <HelpCircle className="w-7 h-7 mr-3 text-[#74A57F]" />
              Help Centre
            </h1>
            <p className="text-slate-600 mt-1">
              Guides, tutorials, and resources
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-12">
        {/* Search Bar - Always visible */}
        <div className="relative mb-6 md:mb-8">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search help articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-3 md:py-4 text-base md:text-lg border-slate-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#74A57F] focus:border-transparent"
            data-testid="help-search"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search Results */}
        {searchTerm.length >= 2 && (
          <div className="mb-8">
            <p className="text-sm text-slate-500 mb-4">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchTerm}"
            </p>
            {searchResults.length > 0 ? (
              <div className="space-y-3">
                {searchResults.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => {
                      setActiveCategory(article.categoryId);
                      setActiveArticle(article);
                      setSearchTerm('');
                    }}
                    className="w-full text-left bg-white rounded-xl p-4 border border-slate-200 hover:border-[#74A57F] hover:shadow-md transition-all"
                  >
                    <h3 className="font-medium text-[#003B5C] mb-1">{article.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">
                      {article.content.substring(0, 150)}...
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No articles found. Try different keywords.</p>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        {!searchTerm && !activeCategory && (
          <>
            {/* Category Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className="bg-white rounded-xl p-5 md:p-6 border border-slate-200 hover:border-[#74A57F] hover:shadow-lg transition-all text-left group"
                    data-testid={`category-${cat.id}`}
                  >
                    <div className={`w-10 h-10 md:w-12 md:h-12 ${cat.color} rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-[#003B5C] text-base md:text-lg mb-1 md:mb-2">
                      {cat.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      {cat.description}
                    </p>
                    <div className="flex items-center text-[#74A57F] text-sm font-medium mt-3 md:mt-4">
                      {cat.id === 'downloads' ? 'View resources' : `${articles[cat.id]?.length || 0} articles`}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick Tips */}
            <div className="bg-gradient-to-br from-[#003B5C] to-[#004d73] rounded-xl p-5 md:p-6 text-white">
              <div className="flex items-start space-x-3">
                <Lightbulb className="w-6 h-6 text-amber-300 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-2">Quick Tip: Mobile Access</h3>
                  <p className="text-white/80 text-sm leading-relaxed">
                    This Help Centre is optimised for mobile devices. Bookmark it on your phone so you can
                    reference guides while standing at your meter box, reviewing fuel receipts, or
                    collecting waste data on-site.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Category View (Articles List) */}
        {!searchTerm && activeCategory && !activeArticle && (
          <>
            <button
              onClick={handleBack}
              className="flex items-center text-slate-600 hover:text-[#003B5C] mb-4 md:mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to categories
            </button>

            {activeCategory === 'downloads' ? (
              /* Downloads Section */
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#003B5C] mb-4 md:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Downloads & Resources
                </h2>
                <div className="grid gap-4">
                  {downloads.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.id}
                        className="bg-white rounded-xl p-5 md:p-6 border border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                      >
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-6 h-6 text-[#003B5C]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#003B5C] mb-1">{item.title}</h3>
                            <p className="text-slate-500 text-sm">{item.description}</p>
                          </div>
                        </div>
                        <Button
                          onClick={item.action}
                          className="bg-[#74A57F] hover:bg-[#5F8575] text-white w-full md:w-auto"
                          data-testid={`download-${item.id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {item.buttonText}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Article List */
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#003B5C] mb-4 md:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {categories.find(c => c.id === activeCategory)?.title}
                </h2>
                <div className="space-y-3">
                  {articles[activeCategory]?.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article)}
                      className="w-full text-left bg-white rounded-xl p-4 md:p-5 border border-slate-200 hover:border-[#74A57F] hover:shadow-md transition-all group"
                      data-testid={`article-${article.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-[#003B5C] group-hover:text-[#74A57F] transition-colors pr-4">
                          {article.title}
                        </h3>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#74A57F] flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Article View */}
        {!searchTerm && activeCategory && activeArticle && (
          <>
            <button
              onClick={handleBack}
              className="flex items-center text-slate-600 hover:text-[#003B5C] mb-4 md:mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {categories.find(c => c.id === activeCategory)?.title}
            </button>

            <article className="bg-white rounded-xl p-5 md:p-8 border border-slate-200 shadow-sm">
              <h1 className="text-xl md:text-2xl font-bold text-[#003B5C] mb-4 md:mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {activeArticle.title}
              </h1>
              <div className="prose prose-slate max-w-none text-sm md:text-base">
                {activeArticle.content.split('\n\n').map((paragraph, idx) => {
                  // Handle headers
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 key={idx} className="font-semibold text-[#003B5C] mt-4 mb-2">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  }
                  // Handle bold text and lists
                  if (paragraph.includes('**') || paragraph.startsWith('•') || paragraph.startsWith('→')) {
                    return (
                      <div key={idx} className="my-2">
                        {paragraph.split('\n').map((line, lineIdx) => {
                          if (line.startsWith('•') || line.startsWith('→')) {
                            return (
                              <p key={lineIdx} className="pl-4 my-1 text-slate-600">
                                {line}
                              </p>
                            );
                          }
                          // Parse bold text
                          const parts = line.split(/(\*\*.*?\*\*)/g);
                          return (
                            <p key={lineIdx} className="my-1 text-slate-600">
                              {parts.map((part, partIdx) =>
                                part.startsWith('**') && part.endsWith('**')
                                  ? <strong key={partIdx} className="text-[#003B5C]">{part.replace(/\*\*/g, '')}</strong>
                                  : part
                              )}
                            </p>
                          );
                        })}
                      </div>
                    );
                  }
                  // Handle tables
                  if (paragraph.includes('|') && paragraph.includes('---')) {
                    const rows = paragraph.split('\n').filter(r => r.trim() && !r.includes('---'));
                    return (
                      <div key={idx} className="overflow-x-auto my-4">
                        <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              {rows[0].split('|').filter(c => c.trim()).map((cell, cellIdx) => (
                                <th key={cellIdx} className="px-3 py-2 text-left font-semibold text-[#003B5C] border-b border-slate-200">
                                  {cell.trim()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.slice(1).map((row, rowIdx) => (
                              <tr key={rowIdx} className="hover:bg-slate-50">
                                {row.split('|').filter(c => c.trim()).map((cell, cellIdx) => (
                                  <td key={cellIdx} className="px-3 py-2 text-slate-600 border-b border-slate-100">
                                    {cell.trim()}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  // Regular paragraph
                  return (
                    <p key={idx} className="my-3 text-slate-600 leading-relaxed">
                      {paragraph}
                    </p>
                  );
                })}
              </div>
            </article>

            {/* Was this helpful */}
            <div className="mt-6 p-4 bg-slate-100 rounded-xl text-center">
              <p className="text-slate-600 text-sm mb-3">Was this article helpful?</p>
              <div className="flex justify-center space-x-3">
                <Button
                  onClick={() => toast.success('Thanks for your feedback!')}
                  variant="outline"
                  size="sm"
                  className="border-slate-300"
                >
                  👍 Yes
                </Button>
                <Button
                  onClick={() => toast.info('Sorry to hear that. We\'ll work on improving this.')}
                  variant="outline"
                  size="sm"
                  className="border-slate-300"
                >
                  👎 No
                </Button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Mobile-friendly contact footer */}
      <footer className="bg-white border-t border-slate-200 px-4 py-6 mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-500 text-sm mb-3">Can't find what you're looking for?</p>
          <Button
            onClick={() => window.location.href = 'mailto:support@lucentsky.com.au'}
            variant="outline"
            className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F] hover:text-white"
          >
            Contact Support
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default HelpCentrePage;
